// overlayKit/BarCompare.tsx — two-bar before→after chart for dramatic value drops.
// Heights are log-scaled so 5000 → 5 reads as a real collapse instead of a hairline.
// Top-left placement to match the universal "padding" Yash set 2026-06-07.
import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate, spring } from "remotion";
import { appear, clamp, FONT, TS, win } from "./utils";
import { useColor } from "./palette";

export type BarCompareProps = {
  /** Before value (numeric, used for log-scale bar height). */
  v1: number;
  /** After value. */
  v2: number;
  /** Display label for v1 (e.g. "5000 PPM"). */
  v1Label: string;
  /** Display label for v2 (e.g. "< 5 PPM"). */
  v2Label: string;
  /** Time the before bar grows (spoken word time). */
  l1S: number;
  /** Time the after bar grows. */
  l2S: number;
  /** Card exit. */
  outS: number;
  /** Optional under-bar captions e.g. "BEFORE" / "AFTER". */
  cap1?: string;
  cap2?: string;
};

export const BarCompare: React.FC<BarCompareProps> = ({
  v1, v2, v1Label, v2Label, l1S, l2S, outS, cap1, cap2,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  const op = win(t, l1S - 0.2, outS);
  if (op <= 0.001) return null;

  const white = useColor("neutral");

  // log10 scale so 5000:5 (1000×) reads as a dramatic drop, not a hairline.
  const log = (x: number) => Math.log10(Math.max(1, x));
  const maxV = Math.max(v1, v2);
  const HMAX = 160;     // tallest bar in px
  const HMIN = 12;      // shortest visible bar
  const h1Target = HMIN + (HMAX - HMIN) * (log(v1) / log(maxV));
  const h2Target = HMIN + (HMAX - HMIN) * (log(v2) / log(maxV));

  const h1 = interpolate(t, [l1S, l1S + 0.55], [0, h1Target], clamp);
  const h2 = interpolate(t, [l2S, l2S + 0.55], [0, h2Target], clamp);

  const op1 = appear(t, l1S, 0.18);
  const op2 = appear(t, l2S, 0.18);

  const arrowOp = interpolate(t, [l2S - 0.2, l2S + 0.15], [0, 1], clamp);
  const sc = spring({ frame: Math.max(0, frame - Math.round(l1S * fps)), fps, config: { damping: 14, stiffness: 90 } });

  const bar: React.CSSProperties = {
    width: 58,
    background: "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.65) 100%)",
    borderRadius: "6px 6px 0 0",
    boxShadow: "0 4px 18px rgba(0,0,0,0.7), 0 0 1px rgba(255,255,255,0.4)",
  };

  return (
    <div style={{
      position: "absolute", top: 18, left: 22,
      fontFamily: FONT, opacity: op, transform: `scale(${sc})`, transformOrigin: "left top",
      display: "flex", flexDirection: "row", alignItems: "flex-end", gap: 32,
    }}>
      {/* before bar */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: op1 }}>
        <div style={{ color: white, fontSize: 22, fontWeight: 800, letterSpacing: "-0.3px", textShadow: TS, marginBottom: 8 }}>{v1Label}</div>
        <div style={{ ...bar, height: h1 }} />
        {cap1 && <div style={{ color: white, fontSize: 12, letterSpacing: "0.2em", marginTop: 6, textShadow: TS }}>{cap1}</div>}
      </div>

      {/* arrow */}
      <div style={{
        height: HMAX, display: "flex", alignItems: "center",
        color: white, fontSize: 36, fontWeight: 900, textShadow: TS, opacity: arrowOp, paddingBottom: 14,
      }}>→</div>

      {/* after bar */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: op2 }}>
        <div style={{ color: white, fontSize: 22, fontWeight: 800, letterSpacing: "-0.3px", textShadow: TS, marginBottom: 8 }}>{v2Label}</div>
        <div style={{ ...bar, height: h2 }} />
        {cap2 && <div style={{ color: white, fontSize: 12, letterSpacing: "0.2em", marginTop: 6, textShadow: TS }}>{cap2}</div>}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
